import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/api';
import { Shield, Plus, Edit2, Trash2, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, pickLocalizedText } from '../../utils/localizedContent';


const ADMIN_TABS = [
  { key: 'dashboard',    label_es: 'Dashboard',                  label_en: 'Dashboard' },
  // Grupo TIENDA
  { key: 'products',     label_es: 'Productos',                  label_en: 'Products' },
  { key: 'stockAlerts',  label_es: 'Avísame (Stock)',            label_en: 'Notify Me (Stock)' },
  { key: 'tables',       label_es: 'Gestión de Mesas',           label_en: 'Table Config' },
  // Grupo RESERVAS
  { key: 'gameRes',      label_es: 'Reservas de Juego',          label_en: 'Game Reservations' },
  { key: 'tableRes',     label_es: 'Reservas de Mesa',           label_en: 'Table Reservations' },
  // Grupo EVENTOS y TCG
  { key: 'events',       label_es: 'Eventos',                    label_en: 'Events' },
  { key: 'boosters',     label_es: 'Boosters / Sets',            label_en: 'Boosters / Sets' },
  { key: 'cardSync',     label_es: 'Sincronización de Cartas',   label_en: 'Card Sync' },
  // Grupo COMUNIDAD
  { key: 'loyalty',      label_es: 'Fidelidad',                  label_en: 'Loyalty' },
  { key: 'users',        label_es: 'Usuarios (solo lectura)',     label_en: 'Users (read-only)' },
  { key: 'collections',  label_es: 'Colecciones',                label_en: 'Collections' },
  // Nota: facturación, roles, ajustes, pedidos y colecciones son solo admin y no se delegan
];

const PERMISSION_COLORS = {
  dashboard:    'bg-blue-500/20 text-blue-300',
  tables:       'bg-emerald-500/20 text-emerald-300',
  gameRes:      'bg-violet-500/20 text-violet-300',
  products:     'bg-orange-500/20 text-orange-300',
  boosters:     'bg-yellow-500/20 text-yellow-300',
  cardSync:     'bg-cyan-500/20 text-cyan-300',
  events:       'bg-pink-500/20 text-pink-300',
  reservations: 'bg-red-500/20 text-red-300',
  stockAlerts:  'bg-amber-500/20 text-amber-300',
  tableRes:     'bg-teal-500/20 text-teal-300',
  loyalty:      'bg-[#C9A962]/20 text-[#C9A962]',
  users:        'bg-indigo-500/20 text-indigo-300',
};

export default function AdminRoles({ roles, onRefresh, lang, setMessage }) {
  const [mode, setMode] = useState(null); // null | 'create' | 'edit' (modo panel)
  const [editingRole, setEditingRole] = useState(null);
  const l = createTextPicker(lang);
  const [form, setForm] = useState({ name: '', permissions: [] });
  const [saving, setSaving] = useState(false);

  const togglePermission = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const openCreate = () => {
    setMode('create');
    setEditingRole(null);
    setForm({ name: '', permissions: [] });
  };

  const openEdit = (role) => {
    setMode('edit');
    setEditingRole(role);
    setForm({ name: role.name, permissions: [...(role.permissions || [])] });
  };

  const cancelForm = () => {
    setMode(null);
    setEditingRole(null);
    setForm({ name: '', permissions: [] });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setMessage(l('El nombre del rol es requerido', 'Role name is required'));
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await axios.post(`${API}/admin/roles`, form);
        setMessage(l('Rol creado correctamente', 'Role created successfully'));
      } else {
        await axios.put(`${API}/admin/roles/${editingRole.role_id}`, form);
        setMessage(l('Rol actualizado correctamente', 'Role updated successfully'));
      }
      cancelForm();
      onRefresh();
    } catch (err) {
      setMessage(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    const msg = l(
      `¿Eliminar el rol "${role.name}"? Esta acción no se puede deshacer.`,
      `Delete role "${role.name}"? This cannot be undone.`
    );
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/roles/${role.role_id}`);
      setMessage(l('Rol eliminado', 'Role deleted'));
      onRefresh();
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const getTabLabel = (key) => {
    const tab = ADMIN_TABS.find(t => t.key === key);
    return tab ? pickLocalizedText(lang, tab.label_es, tab.label_en) : key;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield size={22} className="text-[#C9A962]" />
          <h2 className="text-white font-bold text-xl">
            {l('Roles Personalizados', 'Custom Roles')} ({roles.length})
          </h2>
        </div>
        {mode === null && (
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-xl text-sm font-semibold hover:bg-[#B8984E] transition-colors"
          >
            <Plus size={16} />
            {l('Nuevo Rol', 'New Role')}
          </button>
        )}
      </div>

      {/* Create / Edit Form */}
      {mode !== null && (
        <div className="bg-[#12121A] border border-[#C9A962]/30 rounded-2xl p-6 mb-6">
          <h3 className="text-white font-bold text-lg mb-5">
            {mode === 'create' ? l('Crear Nuevo Rol', 'Create New Role') : l('Editar Rol', 'Edit Role')}
          </h3>

          {/* Name */}
          <div className="mb-5">
            <label className="block text-[#A1A1AA] text-sm font-medium mb-2">
              {l('Nombre del Rol', 'Role Name')}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={l('Ej: Encargado de Tienda', 'E.g: Store Manager')}
              className="w-full max-w-sm bg-[#1C1C28] border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#C9A962] transition-colors"
            />
          </div>

          {/* Permissions checkboxes — grouped by section */}
          <div className="mb-6">
            <label className="block text-[#A1A1AA] text-sm font-medium mb-3">
              {l('Pestañas accesibles', 'Accessible tabs')}
              <span className="ml-2 text-xs text-[#52525B]">
                ({form.permissions.length} {l('seleccionadas', 'selected')})
              </span>
            </label>
            <p className="text-[#52525B] text-xs mb-3">
              {l('Las pestañas de Facturación, Pedidos, Roles, Colecciones y Ajustes son siempre exclusivas del administrador.',
                 'Billing, Orders, Roles, Collections and Settings tabs are always admin-only and cannot be delegated.')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ADMIN_TABS.map(tab => {
                const checked = form.permissions.includes(tab.key);
                return (
                  <label
                    key={tab.key}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                      checked
                        ? 'bg-[#C9A962]/10 border-[#C9A962]/50'
                        : 'bg-[#1C1C28] border-white/5 hover:border-white/20'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        checked ? 'bg-[#C9A962] border-[#C9A962]' : 'border-white/30'
                      }`}
                      onClick={() => togglePermission(tab.key)}
                    >
                      {checked && <Check size={12} className="text-[#0A1612]" strokeWidth={3} />}
                    </div>
                    <span className={`text-sm font-medium ${
                      checked ? 'text-white' : 'text-[#A1A1AA]'
                    }`}>
                      {pickLocalizedText(lang, tab.label_es, tab.label_en)}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A962] text-[#0A1612] rounded-xl text-sm font-semibold hover:bg-[#B8984E] transition-colors disabled:opacity-60"
            >
              <Check size={16} />
              {saving ? l('Guardando...', 'Saving...') : l('Guardar Rol', 'Save Role')}
            </button>
            <button
              onClick={cancelForm}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/5 text-[#A1A1AA] rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors"
            >
              <X size={16} />
              {l('Cancelar', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Roles List */}
      {roles.length === 0 ? (
        <div className="text-center py-16 text-[#52525B]">
          <Shield size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">
            {l('No hay roles personalizados. Crea el primero.', 'No custom roles yet. Create the first one.')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {roles.map(role => (
            <div
              key={role.role_id}
              className="bg-[#12121A] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={18} className="text-[#C9A962]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-semibold text-base">{role.name}</h3>
                    {role.user_count > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-[#3B82F6]/20 text-[#3B82F6]">
                        {role.user_count} {l('usuario(s)', 'user(s)')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(role.permissions || []).length === 0 ? (
                      <span className="text-xs text-[#52525B] italic">{l('Sin permisos', 'No permissions')}</span>
                    ) : (
                      (role.permissions || []).map(perm => (
                        <span
                          key={perm}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${PERMISSION_COLORS[perm] || 'bg-white/10 text-white'}`}
                        >
                          {getTabLabel(perm)}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(role)}
                    className="p-2 rounded-lg bg-white/5 text-[#A1A1AA] hover:bg-[#C9A962]/20 hover:text-[#C9A962] transition-colors"
                    title={l('Editar', 'Edit')}
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(role)}
                    className="p-2 rounded-lg bg-white/5 text-[#A1A1AA] hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    title={l('Eliminar', 'Delete')}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
